// src/queries/feedback.query.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getRequest, postRequest, deleteRequest } from 'utils/http'

const FEEDBACK_QUERY_KEY = 'feedbacks'

// =========================================================
//              FEEDBACK QUERIES
// =========================================================

/**
 * Fetches all feedbacks with pagination.
 * Backend: GET /api/feedbacks
 * @param {number} current - Current page number
 * @param {number} pageSize - Number of items per page
 */
export const getFeedbacks = async (current, pageSize) => {
  return getRequest('feedbacks', {
    params: {
      page: current,
      pageSize: pageSize,
    },
  })
}

/**
 * Hook to fetch feedbacks with pagination.
 */
export const useGetFeedbacks = (current, pageSize) => {
  return useQuery({
    queryKey: [FEEDBACK_QUERY_KEY, current, pageSize],
    queryFn: () => getFeedbacks(current, pageSize),
  })
}

/**
 * Fetches a single feedback by ID.
 * Backend: GET /api/feedbacks/{id}
 */
export const getFeedback = async id => {
  return getRequest(`feedbacks/${id}`)
}

export const useGetFeedback = id => {
  return useQuery({
    queryKey: [FEEDBACK_QUERY_KEY, id],
    queryFn: () => getFeedback(id),
    enabled: !!id,
  })
}

/**
 * Reply to a feedback with site email.
 * Backend: POST /api/feedbacks/{id}/reply
 */
export const replyFeedback = async ({ id, data }) => {
  return postRequest(`feedbacks/${id}/reply`, data)
}

/**
 * Hook to reply to a feedback.
 */
export const useReplyFeedback = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: replyFeedback,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FEEDBACK_QUERY_KEY] })
    },
    onError: error => {
      console.error('Failed to reply to feedback:', error)
    },
  })
}

/**
 * Deletes a feedback entry.
 * Backend: DELETE /api/feedbacks/{id}
 * @param {string} id - The ID of the feedback entry to delete.
 */
export const deleteFeedback = async id => {
  return deleteRequest(`feedbacks/${id}`)
}

/**
 * Hook to delete a feedback entry.
 * On success, it invalidates the 'feedbacks' query to reflect the change.
 */
export const useDeleteFeedback = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteFeedback,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FEEDBACK_QUERY_KEY] })
    },
    onError: error => {
      console.error('Failed to delete feedback:', error)
    },
  })
}
